import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ClassificationChip from '../common/ClassificationChip';
import ContradictionAlert from '../common/ContradictionAlert';
import SuggestionPageShell from '../common/SuggestionPageShell';
import SuggestionReview from '../common/SuggestionReview';
import HighlightedPolicy from '../common/HighlightedPolicy';
import {
  acceptPolicySuggestion,
  applyOutstandingPolicyUpdate,
  deleteSuggestionFeedback,
  dismissPolicySuggestion,
  fetchCriteria,
  fetchCriteriaWithFeedback,
  fetchPolicy,
  fetchPolicySuggestions,
  generatePolicySuggestion,
  skipPolicyFeedback,
} from '../../services/api';
import {
  Criteria,
  LinkedReview,
  OutstandingPolicyFeedback,
  Policy,
  PolicySuggestion,
  PolicySuggestionGenerateResult,
} from '../../types';
import { pickDisplayedSuggestion } from './policySuggestionSelection';

const ReviewCard: React.FC<{
  review: LinkedReview;
  onClick: () => void;
  actions?: React.ReactNode;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}> = ({ review, onClick, actions, selectable, selected, onToggleSelect }) => (
  <Paper
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 1,
      flex: '0 0 560px',
      bgcolor: selected ? 'primary.50' : 'grey.50',
      cursor: 'pointer',
      borderColor: selected ? 'primary.main' : undefined,
      transition: 'background-color 0.15s',
      '&:hover': { bgcolor: selected ? 'primary.50' : 'grey.100' },
    }}
  >
    {(selectable || actions) && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        {selectable ? (
          <Checkbox
            size="small"
            checked={!!selected}
            onClick={event => event.stopPropagation()}
            onChange={event => {
              event.stopPropagation();
              onToggleSelect?.();
            }}
            inputProps={{ 'aria-label': `Select feedback: ${review.sentence}` }}
          />
        ) : (
          <span />
        )}
        {actions}
      </Box>
    )}
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
      Sentence
    </Typography>
    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, lineHeight: 1.4 }}>
      {review.sentence}
    </Typography>
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
      <ClassificationChip value={review.originalClassification} sx={{ minWidth: 90 }} />
      <Typography variant="caption" color="text.secondary">
        &rarr;
      </Typography>
      <ClassificationChip value={review.correctedClassification} sx={{ minWidth: 90 }} />
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
      Reason
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
      {review.reason}
    </Typography>
  </Paper>
);

const ReviewDialog: React.FC<{
  review: OutstandingPolicyFeedback | LinkedReview | null;
  onClose: () => void;
}> = ({ review, onClose }) => (
  <Dialog open={!!review} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      Correction Detail
      <IconButton onClick={onClose} size="small">
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      {review && (
        <>
          {'policyVersion' in review && review.policyVersion != null && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Policy Version {review.policyVersion}
            </Typography>
          )}
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              bgcolor: 'rgba(255, 193, 7, 0.05)',
              borderLeft: '3px solid',
              borderColor: 'warning.main',
              borderRadius: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
            >
              Sentence
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {review.sentence}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Original
              </Typography>
              <ClassificationChip value={review.originalClassification} />
            </Box>
            <Typography variant="body1" color="text.secondary">
              &rarr;
            </Typography>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Corrected
              </Typography>
              <ClassificationChip value={review.correctedClassification} />
            </Box>
          </Box>

          <Box sx={{ p: 1.5, bgcolor: 'rgba(0, 0, 0, 0.03)', borderRadius: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
            >
              Reason for correction
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {review.reason}
            </Typography>
          </Box>
        </>
      )}
    </DialogContent>
  </Dialog>
);

const FeedbackQueue: React.FC<{
  title: string;
  items: Array<OutstandingPolicyFeedback | LinkedReview>;
  onSelect: (review: OutstandingPolicyFeedback | LinkedReview) => void;
  renderActions?: (review: OutstandingPolicyFeedback | LinkedReview) => React.ReactNode;
  selectedIds?: string[];
  onToggleSelect?: (id: string) => void;
}> = ({ title, items, onSelect, renderActions, selectedIds, onToggleSelect }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
      {items.map(review => (
        <ReviewCard
          key={review.id}
          review={review}
          onClick={() => onSelect(review)}
          actions={renderActions?.(review)}
          selectable={!!onToggleSelect}
          selected={selectedIds?.includes(review.id)}
          onToggleSelect={() => onToggleSelect?.(review.id)}
        />
      ))}
    </Box>
  </Box>
);

type Screen = 'select' | 'contradiction' | 'diff' | 'leftover' | 'leftover-edit';

const PolicySuggestions: React.FC = () => {
  const [criteriaList, setCriteriaList] = useState<Criteria[]>([]);
  const [selectedCriteriaId, setSelectedCriteriaId] = useState('');
  const [currentPolicy, setCurrentPolicy] = useState<Policy | null>(null);
  const [suggestion, setSuggestion] = useState<PolicySuggestion | null>(null);
  const [outstandingFeedback, setOutstandingFeedback] = useState<OutstandingPolicyFeedback[]>([]);
  const [selectedFeedbackIds, setSelectedFeedbackIds] = useState<string[]>([]);
  const [draftPolicyText, setDraftPolicyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [screen, setScreen] = useState<Screen>('select');
  const [selectedReview, setSelectedReview] = useState<
    OutstandingPolicyFeedback | LinkedReview | null
  >(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const loadData = useCallback(async () => {
    const criteria = await fetchCriteria();
    setCriteriaList(criteria);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const generatingRef = useRef(false);
  const policyRequestIdRef = useRef(0);
  const displayedSuggestionIdRef = useRef<string | null>(null);
  const criteriaListRef = useRef(criteriaList);
  criteriaListRef.current = criteriaList;

  useEffect(() => {
    displayedSuggestionIdRef.current = suggestion?.id ?? null;
  }, [suggestion]);

  const loadPolicy = useCallback((policyId: string) => {
    const requestId = ++policyRequestIdRef.current;
    fetchPolicy(policyId)
      .then(policy => {
        if (policyRequestIdRef.current === requestId) {
          setCurrentPolicy(policy);
        }
      })
      .catch(() => {
        if (policyRequestIdRef.current === requestId) {
          setCurrentPolicy(null);
        }
      });
  }, []);

  const enterLeftoverPass = useCallback((items: OutstandingPolicyFeedback[]) => {
    setSuggestion(null);
    setOutstandingFeedback(items);
    setSelectedFeedbackIds([]);
    setDraftPolicyText('');
    setScreen(items.length > 0 ? 'leftover' : 'select');
  }, []);

  const applyGenerateResult = useCallback(
    (result: PolicySuggestionGenerateResult) => {
      if ('noSuggestion' in result) {
        enterLeftoverPass(result.outstandingFeedback);
        return;
      }
      setSuggestion(result);
      setOutstandingFeedback([]);
      setSelectedFeedbackIds([]);
      setScreen('contradiction');
    },
    [enterLeftoverPass]
  );

  const loadSuggestionForCriteria = useCallback(async (criteriaId: string) => {
    const suggestions = await fetchPolicySuggestions(criteriaId);
    const next = pickDisplayedSuggestion(suggestions, displayedSuggestionIdRef.current);
    setSuggestion(next);
    if (next) {
      setOutstandingFeedback([]);
      setSelectedFeedbackIds([]);
      setScreen('contradiction');
    } else {
      setScreen('select');
    }
    return next;
  }, []);

  const continueGeneration = useCallback(
    async (criteriaId: string) => {
      if (generatingRef.current) return;
      generatingRef.current = true;
      setGenerating(true);
      setGenerateError('');
      try {
        const criteriaIds = await fetchCriteriaWithFeedback();
        if (!criteriaIds.includes(criteriaId)) {
          setSuggestion(null);
          setOutstandingFeedback([]);
          setSelectedFeedbackIds([]);
          setScreen('select');
          return;
        }
        const result = await generatePolicySuggestion(criteriaId);
        applyGenerateResult(result);
      } catch {
        setGenerateError('Failed to generate suggestion. Please try again.');
      } finally {
        setGenerating(false);
        generatingRef.current = false;
      }
    },
    [applyGenerateResult]
  );

  useEffect(() => {
    if (!selectedCriteriaId) {
      policyRequestIdRef.current++;
      setSuggestion(null);
      setCurrentPolicy(null);
      setOutstandingFeedback([]);
      setSelectedFeedbackIds([]);
      setScreen('select');
      return;
    }
    const criteria = criteriaListRef.current.find(c => c.id === selectedCriteriaId);
    if (criteria?.policyId) {
      loadPolicy(criteria.policyId);
    }
    loadSuggestionForCriteria(selectedCriteriaId).then(pending => {
      if (!pending) {
        continueGeneration(selectedCriteriaId);
      }
    });
  }, [selectedCriteriaId, loadSuggestionForCriteria, continueGeneration, loadPolicy]);

  const refreshCriteriaAndPolicy = async () => {
    const criteria = await fetchCriteria();
    setCriteriaList(criteria);
    const c = criteria.find(item => item.id === selectedCriteriaId);
    if (c?.policyId) {
      loadPolicy(c.policyId);
    }
  };

  const leaveCurrentSuggestion = () => {
    setGenerating(true);
    setSuggestion(null);
    setScreen('select');
  };

  const handleDismiss = async () => {
    if (!suggestion) return;
    const criteriaId = suggestion.criteriaId;
    await dismissPolicySuggestion(suggestion.id);
    leaveCurrentSuggestion();
    await refreshCriteriaAndPolicy();
    await continueGeneration(criteriaId);
  };

  const handleSave = async (finalText: string) => {
    if (!suggestion) return;
    const criteriaId = suggestion.criteriaId;
    await acceptPolicySuggestion(suggestion.id, finalText);
    leaveCurrentSuggestion();
    await refreshCriteriaAndPolicy();
    await continueGeneration(criteriaId);
  };

  const handleDeleteFeedback = async () => {
    if (!suggestion) return;
    const criteriaId = suggestion.criteriaId;
    await deleteSuggestionFeedback(suggestion.id);
    leaveCurrentSuggestion();
    await refreshCriteriaAndPolicy();
    await continueGeneration(criteriaId);
  };

  const handleSkipFeedback = async (reviewId: string) => {
    const result = await skipPolicyFeedback(reviewId);
    setOutstandingFeedback(result.outstandingFeedback);
    setSelectedFeedbackIds(ids => ids.filter(id => id !== reviewId));
    if (result.outstandingFeedback.length === 0) {
      setScreen('select');
    }
  };

  const handleRetryGenerate = async () => {
    if (!selectedCriteriaId) return;
    await continueGeneration(selectedCriteriaId);
  };

  const handleToggleSelect = (reviewId: string) => {
    setSelectedFeedbackIds(ids =>
      ids.includes(reviewId) ? ids.filter(id => id !== reviewId) : [...ids, reviewId]
    );
  };

  const handleStartLeftoverEdit = () => {
    setDraftPolicyText(currentPolicy?.text ?? '');
    setScreen('leftover-edit');
  };

  const handleConfirmLeftover = async () => {
    if (!selectedCriteriaId) return;
    const result = await applyOutstandingPolicyUpdate(
      selectedCriteriaId,
      draftPolicyText,
      selectedFeedbackIds
    );
    await refreshCriteriaAndPolicy();
    enterLeftoverPass(result.outstandingFeedback);
  };

  if (loading) {
    return null;
  }

  const leftoverEditingItems = outstandingFeedback.filter(item =>
    selectedFeedbackIds.includes(item.id)
  );
  const leftoverEditDirty = draftPolicyText !== (currentPolicy?.text ?? '');
  const showContent =
    (!!suggestion && screen !== 'select') || screen === 'leftover' || screen === 'leftover-edit';

  return (
    <SuggestionPageShell
      title="Policy suggestions"
      criteriaList={criteriaList}
      selectedCriteriaId={selectedCriteriaId}
      onCriteriaChange={id => {
        setSelectedCriteriaId(id);
        setScreen('select');
        setOutstandingFeedback([]);
        setSelectedFeedbackIds([]);
      }}
      placeholderText="Select a criterion to view or generate suggestions"
      generating={generating}
      generateError={generateError}
      hasSuggestion={showContent}
    >
      {suggestion && screen === 'contradiction' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            height: 'calc(100% - 56px)',
            minHeight: 0,
            maxWidth: 1200,
            mx: 'auto',
          }}
        >
          <ContradictionAlert reason={suggestion.contradictionReason} />

          <FeedbackQueue
            title={`Supporting Corrections (${suggestion.linkedReviews.length})`}
            items={suggestion.linkedReviews}
            onSelect={setSelectedReview}
          />

          <Box sx={{ flex: 1, minHeight: 300, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Policy
            </Typography>
            {currentPolicy && (
              <HighlightedPolicy
                policyText={currentPolicy.text}
                conflictingText={suggestion.conflictingText}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, pb: 1 }}>
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => setScreen('diff')}>
              Update Policy
            </Button>
            <Button
              variant="outlined"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setConfirmDelete(true)}
            >
              Delete Feedback
            </Button>
            <Button variant="text" onClick={handleDismiss}>
              Skip
            </Button>
          </Box>
        </Box>
      )}

      {suggestion && screen === 'diff' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            height: 'calc(100% - 56px)',
            maxWidth: 1200,
            mx: 'auto',
          }}
        >
          {suggestion.linkedReviews.length > 0 && (
            <FeedbackQueue
              title={`Feedback (${suggestion.linkedReviews.length})`}
              items={suggestion.linkedReviews}
              onSelect={setSelectedReview}
            />
          )}

          <Box sx={{ flex: 1, minHeight: 300, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Policy Suggestions
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {currentPolicy && (
                <SuggestionReview
                  original={currentPolicy.text}
                  suggestions={suggestion.suggestedEdits}
                  onBack={() => setScreen('contradiction')}
                  onSave={handleSave}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}

      {screen === 'leftover' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: 'calc(100% - 56px)',
            minHeight: 0,
            maxWidth: 1200,
            mx: 'auto',
          }}
        >
          <FeedbackQueue
            title={`Outstanding Policy Feedback (${outstandingFeedback.length})`}
            items={outstandingFeedback}
            onSelect={setSelectedReview}
            selectedIds={selectedFeedbackIds}
            onToggleSelect={handleToggleSelect}
            renderActions={review => (
              <Box
                sx={{ display: 'flex', gap: 0.5 }}
                onClick={event => event.stopPropagation()}
                onMouseDown={event => event.stopPropagation()}
              >
                <Button
                  size="small"
                  onClick={event => {
                    event.stopPropagation();
                    handleSkipFeedback(review.id);
                  }}
                >
                  Skip
                </Button>
              </Box>
            )}
          />
          <Box sx={{ flex: 1, minHeight: 200, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Policy
            </Typography>
            {currentPolicy && (
              <HighlightedPolicy policyText={currentPolicy.text} conflictingText={[]} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, pb: 1 }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              disabled={selectedFeedbackIds.length === 0}
              onClick={handleStartLeftoverEdit}
            >
              Update Policy
            </Button>
            <Button variant="outlined" onClick={handleRetryGenerate}>
              Try generating again
            </Button>
          </Box>
        </Box>
      )}

      {screen === 'leftover-edit' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: 'calc(100% - 56px)',
            minHeight: 0,
            maxWidth: 1200,
            mx: 'auto',
          }}
        >
          <FeedbackQueue
            title={`Outstanding Policy Feedback (${leftoverEditingItems.length})`}
            items={leftoverEditingItems}
            onSelect={setSelectedReview}
          />
          <Box sx={{ flex: 1, minHeight: 300, display: 'flex', flexDirection: 'column' }}>
            <TextField
              label="Policy"
              value={draftPolicyText}
              onChange={event => setDraftPolicyText(event.target.value)}
              multiline
              minRows={12}
              fullWidth
              sx={{ flex: 1 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, pb: 1 }}>
            <Button variant="text" onClick={() => setScreen('leftover')}>
              Back
            </Button>
            <Button
              variant="contained"
              disabled={!leftoverEditDirty || selectedFeedbackIds.length === 0}
              onClick={handleConfirmLeftover}
            >
              Confirm changes
            </Button>
          </Box>
        </Box>
      )}

      <ReviewDialog review={selectedReview} onClose={() => setSelectedReview(null)} />

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete Feedback</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete {suggestion?.linkedReviews.length ?? 0} reviewer correction
            {(suggestion?.linkedReviews.length ?? 0) !== 1 ? 's' : ''}. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmDelete(false);
              handleDeleteFeedback();
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </SuggestionPageShell>
  );
};

export default PolicySuggestions;
